import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import './responsive.css';
import FileImporter from './components/FileImporter';
import StockTable from './components/StockTable';
import ReorderRecommendations from './components/ReorderRecommendations';
import SupplierConfig from './components/SupplierConfig';
import InventoryValue from './components/InventoryValue';
import DataBackup from './components/DataBackup';
import SuppliesStock from './components/SuppliesStock';
import ROIAnalysis from './components/ROIAnalysis';
import AmazonImport from './components/AmazonImport';
import apiService from './services/apiService';

function App() {
  const [stockFBMData, setStockFBMData] = useState([]);
  const [stockFBAData, setStockFBAData] = useState([]);
  const [mergedStockData, setMergedStockData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [apiProducts, setApiProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('import');
  const [previousTab, setPreviousTab] = useState('');
  const [supplierConfig, setSupplierConfig] = useState(null);
  const [stockAtHome, setStockAtHome] = useState({}); // Stock FBA à la maison
  const [stockInTransit, setStockInTransit] = useState({}); // Stock en transit
  const [fbaTransferDelay, setFbaTransferDelay] = useState(10); // Délai par défaut: 10 jours
  const [supplierTransitDelay, setSupplierTransitDelay] = useState(30); // Délai par défaut: 30 jours
  
  // Référence pour suivre les modifications
  const hasChanges = useRef(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [pendingTab, setPendingTab] = useState('');
  
  // État pour le menu burger mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fonction pour charger les produits depuis l'API
  const loadProductsFromAPI = async () => {
    try {
      console.log('🔄 Chargement des produits depuis l\'API...');
      const response = await fetch('/api/products');
      if (response.ok) {
        const products = await response.json();
        console.log('✅ Produits chargés depuis l\'API:', products.length, 'produits');
        setApiProducts(products);
      } else {
        console.error('❌ Erreur lors du chargement des produits:', response.statusText);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des produits:', error);
    }
  };

  // Initialize API service and load data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize API service (handles migration if needed)
        await apiService.initialize();
        
        // Charger les produits existants
        await loadProductsFromAPI();
        
        // Load data from API or localStorage fallback
        await loadInitialData();
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Load from localStorage as fallback
        await loadFromLocalStorage();
      }
    };
    
    initializeApp();
  }, []);
  
  const loadInitialData = async () => {
    try {
      // Load suppliers and create supplier config structure
      const suppliers = await apiService.getSuppliers();
      const products = await apiService.getProducts();
      
      // Reconstruct supplier config from API data
      const supplierConfig = {
        suppliers: suppliers,
        productSuppliers: {}
      };
      
      // Build product-supplier mapping
      products.forEach(product => {
        if (product.supplier_id) {
          supplierConfig.productSuppliers[product.sku] = product.supplier_id;
        }
      });
      
      setSupplierConfig(supplierConfig);
      
      // Load stock data
      const stockAtHomeData = {};
      const stockInTransitData = {};
      
      products.forEach(product => {
        if (product.home_quantity > 0) {
          stockAtHomeData[product.sku] = product.home_quantity;
        }
        if (product.transit_quantity > 0) {
          stockInTransitData[product.sku] = {
            quantity: product.transit_quantity,
            orderDate: product.transit_order_date,
            supplierId: product.transit_supplier_id
          };
        }
      });
      
      setStockAtHome(stockAtHomeData);
      setStockInTransit(stockInTransitData);
      
      // Load settings
      const settings = await apiService.getSettings();
      setFbaTransferDelay(parseInt(settings.fbaTransferDelay) || 10);
      setSupplierTransitDelay(parseInt(settings.supplierTransitDelay) || 30);
      
    } catch (error) {
      console.error('Failed to load initial data from API:', error);
      throw error;
    }
  };
  
  const loadFromLocalStorage = async () => {
    try {
      const savedConfig = localStorage.getItem('supplierConfig');
      if (savedConfig) {
        setSupplierConfig(JSON.parse(savedConfig));
      }
      
      const savedHomeStock = localStorage.getItem('stockAtHome');
      if (savedHomeStock) {
        setStockAtHome(JSON.parse(savedHomeStock));
      }
      
      const savedTransitStock = localStorage.getItem('stockInTransit');
      if (savedTransitStock) {
        setStockInTransit(JSON.parse(savedTransitStock));
      }
      
      const savedFbaDelay = localStorage.getItem('fbaTransferDelay');
      if (savedFbaDelay) {
        setFbaTransferDelay(parseInt(savedFbaDelay) || 10);
      }
      
      const savedSupplierDelay = localStorage.getItem('supplierTransitDelay');
      if (savedSupplierDelay) {
        setSupplierTransitDelay(parseInt(savedSupplierDelay) || 30);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  // Fusionner les données de stock FBM et FBA et ajouter les données de vente
  const mergeAllData = (fbm, fba, sales) => {
    console.log("Fusion des données - FBM:", fbm.length, "produits, FBA:", fba.length, "produits, Sales:", sales ? sales.length : 0, "produits");
    
    // Obtenir tous les SKUs uniques
    const allSkus = new Set([
      ...fbm.map(item => (item.sku || item.SKU || item['seller-sku'] || '').toLowerCase()),
      ...fba.map(item => (item['seller-sku'] || '').toLowerCase())
    ]);
    
    console.log("Nombre total de SKUs uniques:", allSkus.size);
    
    // Créer un tableau fusionné
    const merged = Array.from(allSkus).map(skuLower => {
      // Chercher l'élément dans FBM (faire correspondance non sensible à la casse)
      const fbmItem = fbm.find(item => 
        (item.sku || '').toLowerCase() === skuLower || 
        (item.SKU || '').toLowerCase() === skuLower ||
        (item['seller-sku'] || '').toLowerCase() === skuLower
      );
      
      // Chercher l'élément dans FBA
      const fbaItem = fba.find(item => 
        (item['seller-sku'] || '').toLowerCase() === skuLower
      );
      
      // Chercher l'élément dans les données de vente
      const salesItem = sales ? sales.find(item => {
        const salesSku = (item.SKU || item.sku || '').toLowerCase();
        return salesSku === skuLower;
      }) : null;
      
      // Déterminer le SKU à afficher (utiliser la version originale, pas en minuscules)
      const displaySku = 
        (fbmItem && (fbmItem.sku || fbmItem.SKU || fbmItem['seller-sku'])) || 
        (fbaItem && fbaItem['seller-sku']) || 
        skuLower;
      
      // Récupérer les valeurs de stock à la maison
      const homeStockValue = stockAtHome[displaySku] || 0;
      
      // Récupérer les valeurs de stock en transit et informations associées
      let transitStockValue = 0;
      let transitOrderDate = null;
      let transitSupplierId = null;
      
      if (stockInTransit[displaySku]) {
        // Nouveau format : objet avec quantity, orderDate et supplierId
        if (typeof stockInTransit[displaySku] === 'object') {
          transitStockValue = stockInTransit[displaySku].quantity || 0;
          transitOrderDate = stockInTransit[displaySku].orderDate || null;
          transitSupplierId = stockInTransit[displaySku].supplierId || null;
        } else {
          // Ancien format : juste un nombre
          transitStockValue = stockInTransit[displaySku] || 0;
        }
      }
      
      // Fusionner les données
      const mergedItem = {
        sku: displaySku,
        fbmQuantity: 0,
        fbaQuantity: 0,
        homeQuantity: homeStockValue,
        transitQuantity: transitStockValue,
        transitOrderDate: transitOrderDate,
        transitSupplierId: transitSupplierId,
        dailySales: 0 // Par défaut
      };
      
      // Ajouter les données FBM si disponibles
      if (fbmItem) {
        console.log("Match FBM trouvé pour", displaySku);
        mergedItem.fbmQuantity = parseInt(fbmItem.quantity || 0);
        mergedItem.asin = fbmItem.asin || fbmItem.ASIN || fbmItem['product-id'];
        mergedItem.productName = fbmItem['item-name'] || '';
        mergedItem.price = fbmItem.price || '';
      }
      
      // Ajouter les données FBA si disponibles
      if (fbaItem) {
        console.log("Match FBA trouvé pour", displaySku);
        mergedItem.fbaQuantity = parseInt(fbaItem['Quantity Available'] || 0);
        mergedItem.asin = mergedItem.asin || fbaItem.asin;
      }
      
      // Ajouter les données de vente si disponibles
      if (salesItem) {
        console.log("Match Sales trouvé pour", displaySku);
        // Utiliser la colonne "AVG Daily Sales (7 days)" du fichier Excel
        mergedItem.dailySales = parseFloat(salesItem['AVG Daily Sales (7 days)'] || 0);
        
        // Ajouter d'autres infos produit si manquantes
        if (!mergedItem.productName) {
          mergedItem.productName = salesItem['Product Title'] || '';
        }
        if (!mergedItem.asin) {
          mergedItem.asin = salesItem.ASIN || '';
        }
      }
      
      // Calculer la quantité totale (FBM + FBA uniquement)
      mergedItem.fbaFbmTotal = mergedItem.fbmQuantity + mergedItem.fbaQuantity;
      
      // Calculer la quantité totale (FBM + FBA + à la maison + en transit)
      mergedItem.quantity = mergedItem.fbmQuantity + mergedItem.fbaQuantity + 
                           homeStockValue + transitStockValue;
      
      return mergedItem;
    });
    
    // Filtrer les produits sans stock FBA/FBM (on conserve ceux qui ont du stock à la maison ou en transit)
    const filteredMerged = merged.filter(item => item.fbaFbmTotal > 0 || item.homeQuantity > 0 || item.transitQuantity > 0);
    console.log("Après fusion et filtrage:", filteredMerged.length, "produits");
    
    return filteredMerged;
  };

  // Fonction pour traiter les fichiers importés
  const handleDataImported = (data, fileName, fileType) => {
    console.log(`Fichier importé: ${fileName}, Type: ${fileType}, Nombre d'entrées: ${data.length}`);
    
    // Traiter selon le type de fichier
    switch(fileType) {
      case 'stock-fbm':
      case 'stock-fbm-listings':
        // Filtrer les produits et reformater pour correspondre au format du stock FBM
        const fbmData = data.map(item => ({
          sku: item['seller-sku'] || item.sku || item.SKU,
          quantity: parseInt(item.quantity || 0),
          asin: item.asin || item.ASIN || item['product-id'],
          price: item.price,
          'item-name': item['item-name']
        }));
        
        setStockFBMData(fbmData);
        console.log("Données FBM mises à jour:", fbmData.length, "produits");
        
        // Mise à jour immédiate du stock fusionné
        if (stockFBAData.length > 0) {
          const merged = mergeAllData(fbmData, stockFBAData, salesData);
          setMergedStockData(merged);
          handleTabChange('stock');
        }
        
        alert(`Données de stock FBM importées : ${fbmData.length} produits`);
        break;
        
      case 'stock-fba':
        setStockFBAData(data);
        console.log("Données FBA mises à jour:", data.length, "produits");
        
        // Mise à jour immédiate du stock fusionné
        if (stockFBMData.length > 0) {
          const merged = mergeAllData(stockFBMData, data, salesData);
          setMergedStockData(merged);
          handleTabChange('stock');
        }
        
        alert(`Données de stock FBA importées : ${data.length} produits`);
        break;
        
      case 'sales-data':
        setSalesData(data);
        console.log("Données de vente mises à jour:", data.length, "produits");
        
        // Mise à jour immédiate du stock fusionné si on a déjà des données de stock
        if (stockFBMData.length > 0 || stockFBAData.length > 0) {
          const merged = mergeAllData(stockFBMData, stockFBAData, data);
          setMergedStockData(merged);
          handleTabChange('stock');
        }
        
        alert(`Données de ventes importées : ${data.length} enregistrements`);
        break;
        
      default:
        alert(`Données importées de type inconnu: ${fileType}`);
        console.log('Données importées de type inconnu:', fileType, data);
    }
  };

  const handleSuppliersConfigured = async (config) => {
    try {
      // Update suppliers via API
      // Note: This will be handled by the SupplierConfig component
      // which will call apiService methods directly
      setSupplierConfig(config);
      hasChanges.current = true;
    } catch (error) {
      console.error('Failed to update supplier config:', error);
    }
  };

  // Méthode pour mettre à jour le stock FBA à la maison
  const updateHomeStock = async (sku, quantity) => {
    const newQuantity = parseInt(quantity) || 0;
    console.log(`Mise à jour du stock à la maison pour ${sku}: ${newQuantity}`);
    
    try {
      // Update via API
      await apiService.updateHomeStock(sku, newQuantity);
      
      // Recharger les produits depuis l'API pour synchroniser l'interface
      await loadProductsFromAPI();
      
      // Update local state for fallback
      const newStockAtHome = {
        ...stockAtHome,
        [sku]: newQuantity
      };
      
      setStockAtHome(newStockAtHome);
      
      // Mise à jour du stockToUse for fallback
      const updatedMergedData = mergedStockData.map(item => {
        if (item.sku === sku) {
          // Mettre à jour à la fois homeQuantity et quantity (total)
          const updatedItem = {
            ...item,
            homeQuantity: newQuantity,
            quantity: item.fbmQuantity + item.fbaQuantity + newQuantity + (item.transitQuantity || 0)
          };
          console.log("Après mise à jour:", updatedItem);
          return updatedItem;
        }
        return item;
      });
      
      setMergedStockData(updatedMergedData);
      hasChanges.current = true;
    } catch (error) {
      console.error('Failed to update home stock:', error);
      // Fallback to localStorage is handled by apiService
    }
  };
  


  // Méthode mise à jour pour mettre à jour le stock en transit avec date et fournisseur
  const updateTransitStock = async (sku, quantity, orderDate, supplierId) => {
    const newQuantity = parseInt(quantity) || 0;
    console.log(`Mise à jour du stock en transit pour ${sku}: ${newQuantity}, date: ${orderDate}, fournisseur: ${supplierId}`);
    
    try {
      // Update via API
      await apiService.updateTransitStock(sku, newQuantity, orderDate, supplierId);
      
      // Recharger les produits depuis l'API pour synchroniser l'interface
      await loadProductsFromAPI();
      
      // Update local state for fallback
      let newStockInTransit = { ...stockInTransit };
      
      if (newQuantity > 0) {
        newStockInTransit[sku] = {
          quantity: newQuantity,
          orderDate: orderDate || null,
          supplierId: supplierId || null
        };
      } else {
        delete newStockInTransit[sku];
      }
      
      setStockInTransit(newStockInTransit);
      
      // Mise à jour du stockToUse for fallback
      const updatedMergedData = mergedStockData.map(item => {
        if (item.sku === sku) {
          const updatedItem = {
            ...item,
            transitQuantity: newQuantity,
            transitOrderDate: orderDate,
            transitSupplierId: supplierId,
            quantity: item.fbmQuantity + item.fbaQuantity + (item.homeQuantity || 0) + newQuantity
          };
          console.log("Après mise à jour du transit:", updatedItem);
          return updatedItem;
        }
        return item;
      });
      
      setMergedStockData(updatedMergedData);
      hasChanges.current = true;
    } catch (error) {
      console.error('Failed to update transit stock:', error);
      // Fallback to localStorage is handled by apiService
    }
  };
  
  // Fonction pour transférer le stock en transit vers le stock à la maison
  const transferTransitToHome = async (sku) => {
    console.log(`Début du transfert pour ${sku}`);
    
    try {
      // Transfer via API
      const result = await apiService.transferTransitToHome(sku);
      
      // Recharger les produits depuis l'API pour synchroniser l'interface
      await loadProductsFromAPI();
      
      // Update local state based on API response for fallback
      const newStockAtHome = {
        ...stockAtHome,
        [sku]: result.new_home_quantity
      };
      
      const newStockInTransit = { ...stockInTransit };
      delete newStockInTransit[sku];
      
      setStockAtHome(newStockAtHome);
      setStockInTransit(newStockInTransit);
      
      // Update merged data for fallback
      const updatedMergedData = mergedStockData.map(product => {
        if (product.sku === sku) {
          return {
            ...product,
            homeQuantity: result.new_home_quantity,
            transitQuantity: 0,
            transitOrderDate: null,
            transitSupplierId: null,
            quantity: product.fbmQuantity + product.fbaQuantity + result.new_home_quantity
          };
        }
        return product;
      });
      
      setMergedStockData(updatedMergedData);
      
      console.log("Transfert terminé avec succès");
      hasChanges.current = true;
      return true;
    } catch (error) {
      console.error('Failed to transfer stock:', error);
      return false;
    }
  };

  // Méthode pour mettre à jour le délai de transfert FBA
  const updateFbaTransferDelay = async (days) => {
    const newDelay = parseInt(days) || 10;
    try {
      await apiService.updateSetting('fbaTransferDelay', newDelay);
      setFbaTransferDelay(newDelay);
      hasChanges.current = true;
    } catch (error) {
      console.error('Failed to update FBA delay:', error);
    }
  };
  
  // Méthode pour mettre à jour le délai de transit du fournisseur
  const updateSupplierTransitDelay = async (days) => {
    const newDelay = parseInt(days) || 30;
    try {
      await apiService.updateSetting('supplierTransitDelay', newDelay);
      setSupplierTransitDelay(newDelay);
      hasChanges.current = true;
    } catch (error) {
      console.error('Failed to update supplier delay:', error);
    }
  };

  // Fonction pour basculer le menu mobile
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Gestion de changement d'onglet avec sauvegarde
  const handleTabChange = (tabName) => {
    // Fermer le menu mobile lors du changement d'onglet
    setMobileMenuOpen(false);
    
    // Si on est sur les onglets qui ont des données modifiables et qu'il y a eu des changements
    if ((activeTab === 'stock' || activeTab === 'inventory' || activeTab === 'config' || activeTab === 'roi') && hasChanges.current) {
      setPendingTab(tabName);
      setShowBackupPrompt(true);
    } else {
      setPreviousTab(activeTab);
      setActiveTab(tabName);
      // Réinitialiser le flag de changements
      hasChanges.current = false;
    }
  };

  // Fonction pour exporter les données et continuer
  const exportAndContinue = async () => {
    try {
      // Export data via API service
      const dataToExport = await apiService.exportData();
      
      // Convertir en chaîne JSON et créer un blob
      const jsonString = JSON.stringify(dataToExport, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Créer un lien de téléchargement
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Configurer le lien de téléchargement
      const date = new Date().toISOString().slice(0, 10);
      const time = new Date().toISOString().slice(11, 19).replace(/:/g, '-');
      link.href = url;
      link.setAttribute('download', `amazon-stock-backup-${date}_${time}.json`);
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Continuer vers l'onglet demandé
      setShowBackupPrompt(false);
      setPreviousTab(activeTab);
      setActiveTab(pendingTab);
      hasChanges.current = false;
    } catch (error) {
      console.error('Erreur lors de l\'export des données:', error);
      alert('Erreur lors de la sauvegarde des données.');
    }
  };

  // Continuer sans sauvegarder
  const continueWithoutBackup = () => {
    setShowBackupPrompt(false);
    setPreviousTab(activeTab);
    setActiveTab(pendingTab);
    hasChanges.current = false;
  };

  // Annuler le changement d'onglet
  const cancelTabChange = () => {
    setShowBackupPrompt(false);
    setPendingTab('');
  };

  // Déterminer le stock à utiliser pour les différents composants
  // PRIORITÉ CHANGÉE : utiliser les données API comme base pour préserver FC Transfer
  const stockToUse = apiProducts.length > 0 ? apiProducts.map(product => ({
                       sku: product.sku,
                       asin: product.asin,
                       productName: product.product_name,
                       fbmQuantity: product.fbm_quantity || 0,
                       fbaQuantity: product.fba_available || 0,
                       fcTransfer: product.fc_transfer || 0,
                       homeQuantity: product.home_quantity || 0,
                       transitQuantity: product.transit_quantity || 0,
                       transitOrderDate: product.transit_order_date,
                       transitSupplierId: product.transit_supplier_id,
                       quantity: (product.fbm_quantity || 0) + (product.fba_available || 0) + (product.home_quantity || 0) + (product.transit_quantity || 0),
                       dailySales: product.ventes_par_jour || 0,
                       supplier_lead_time: product.supplier_lead_time,
                       supplier_id: product.supplier_id
                     })) :
                     mergedStockData.length > 0 ? mergedStockData : 
                     stockFBMData.length > 0 && stockFBAData.length > 0 ? mergeAllData(stockFBMData, stockFBAData, salesData) :
                     stockFBMData.length > 0 ? stockFBMData.map(item => {
                       const sku = item.sku || item.SKU || item['seller-sku'];
                       const fbmQty = parseInt(item.quantity || 0);
                       
                       // Récupérer les informations de stock à la maison
                       const homeQty = stockAtHome[sku] || 0;
                       
                       // Récupérer les informations de stock en transit
                       let transitQty = 0;
                       let transitOrderDate = null;
                       let transitSupplierId = null;
                       if (stockInTransit[sku]) {
                         if (typeof stockInTransit[sku] === 'object') {
                           transitQty = stockInTransit[sku].quantity || 0;
                           transitOrderDate = stockInTransit[sku].orderDate;
                           transitSupplierId = stockInTransit[sku].supplierId;
                         } else {
                           transitQty = stockInTransit[sku] || 0;
                         }
                       }
                       
                       return {
                         ...item,
                         sku: sku,
                         fbmQuantity: fbmQty,
                         fbaQuantity: 0,
                         homeQuantity: homeQty,
                         transitQuantity: transitQty,
                         transitOrderDate: transitOrderDate,
                         transitSupplierId: transitSupplierId,
                         quantity: fbmQty + homeQty + transitQty
                       };
                     }) : 
                     stockFBAData.length > 0 ? stockFBAData.map(item => {
                       const sku = item['seller-sku'];
                       const fbaQty = parseInt(item['Quantity Available'] || 0);
                       
                       // Récupérer les informations de stock à la maison
                       const homeQty = stockAtHome[sku] || 0;
                       
                       // Récupérer les informations de stock en transit
                       let transitQty = 0;
                       let transitOrderDate = null;
                       let transitSupplierId = null;
                       if (stockInTransit[sku]) {
                         if (typeof stockInTransit[sku] === 'object') {
                           transitQty = stockInTransit[sku].quantity || 0;
                           transitOrderDate = stockInTransit[sku].orderDate;
                           transitSupplierId = stockInTransit[sku].supplierId;
                         } else {
                           transitQty = stockInTransit[sku] || 0;
                         }
                       }
                       
                       return {
                         sku: sku,
                         asin: item.asin,
                         fbmQuantity: 0,
                         fbaQuantity: fbaQty,
                         homeQuantity: homeQty,
                         transitQuantity: transitQty,
                         transitOrderDate: transitOrderDate,
                         transitSupplierId: transitSupplierId,
                         quantity: fbaQty + homeQty + transitQty
                       };
                     }) : [];

  return (
    <div className="App">
      <header className="App-header">
        <h1>Gestion de Stock Amazon FBM/FBA</h1>
        <DataBackup />
      </header>
      
      <nav className="App-nav">
        {/* Menu burger pour mobile */}
        <div 
          className={`burger-menu ${mobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Menu de navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
        
        {/* Boutons de navigation */}
        <div className={`nav-buttons ${mobileMenuOpen ? 'open' : ''}`}>
          <button 
            className={activeTab === 'import' ? 'active' : ''} 
            onClick={() => handleTabChange('import')}
          >
            📂 Importation
          </button>
          <button 
            className={activeTab === 'amazon-import' ? 'active' : ''} 
            onClick={() => handleTabChange('amazon-import')}
          >
            🛍️ Import Amazon
          </button>
          <button 
            className={activeTab === 'stock' ? 'active' : ''} 
            onClick={() => handleTabChange('stock')}
          >
            📦 Stock marchandises
          </button>
          <button 
            className={activeTab === 'supplies' ? 'active' : ''} 
            onClick={() => handleTabChange('supplies')}
          >
            🔧 Stock fournitures
          </button>
          <button 
            className={activeTab === 'inventory' ? 'active' : ''} 
            onClick={() => handleTabChange('inventory')}
          >
            📊 Inventaire Comptable
          </button>
          <button 
            className={activeTab === 'roi' ? 'active' : ''} 
            onClick={() => handleTabChange('roi')}
          >
            💰 Analyse ROI
          </button>
          <button 
            className={activeTab === 'reorder' ? 'active' : ''} 
            onClick={() => handleTabChange('reorder')}
          >
            🔄 Réapprovisionnement
          </button>
          <button 
            className={activeTab === 'config' ? 'active' : ''} 
            onClick={() => handleTabChange('config')}
          >
            ⚙️ Configuration
          </button>
        </div>
      </nav>
      
      <main className="App-main">
        {activeTab === 'import' && (
          <FileImporter onDataImported={handleDataImported} />
        )}
        
        {activeTab === 'amazon-import' && (
          <AmazonImport onImportSuccess={loadProductsFromAPI} />
        )}
        
        {activeTab === 'stock' && stockToUse.length > 0 && (
          <StockTable
            stockData={stockToUse}
            updateHomeStock={updateHomeStock}
            updateTransitStock={updateTransitStock}
            transferTransitToHome={transferTransitToHome}
            fbaTransferDelay={fbaTransferDelay}
            updateFbaTransferDelay={updateFbaTransferDelay}
            supplierTransitDelay={supplierTransitDelay}
            updateSupplierTransitDelay={updateSupplierTransitDelay}
            supplierConfig={supplierConfig}
          />
        )}
        
        {activeTab === 'supplies' && (
          <SuppliesStock />
        )}
        
        {activeTab === 'inventory' && stockToUse.length > 0 && (
          <InventoryValue 
            stockData={stockToUse}
          />
        )}
        
        {activeTab === 'roi' && stockToUse.length > 0 && (
          <ROIAnalysis 
            stockData={stockToUse}
            supplierConfig={supplierConfig}
            fbaTransferDelay={fbaTransferDelay}
            supplierTransitDelay={supplierTransitDelay}
          />
        )}
        
        {activeTab === 'reorder' && stockToUse.length > 0 && (
          <ReorderRecommendations 
            stockData={stockToUse}
            supplierConfig={supplierConfig}
            fbaTransferDelay={fbaTransferDelay}
            supplierTransitDelay={supplierTransitDelay}
          />
        )}
        
        {activeTab === 'config' && (
          <SupplierConfig 
            stockData={stockToUse}
            onSuppliersConfigured={handleSuppliersConfigured}
          />
        )}
        
        
{/* Popup de confirmation pour la sauvegarde */}
        {showBackupPrompt && (
          <div className="backup-prompt-overlay">
            <div className="backup-prompt">
              <h3>Souhaitez-vous sauvegarder vos données ?</h3>
              <p>Des modifications ont été détectées. Si vous changez d'onglet sans sauvegarder, vous pourriez perdre ces modifications en cas de changement de navigateur ou de suppression des données.</p>
              <div className="backup-prompt-buttons">
                <button 
                  onClick={exportAndContinue}
                  className="save-btn"
                >
                  Sauvegarder et continuer
                </button>
                <button 
                  onClick={continueWithoutBackup}
                  className="skip-btn"
                >
                  Continuer sans sauvegarder
                </button>
                <button 
                  onClick={cancelTabChange}
                  className="cancel-btn"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="App-footer">
        <div className="status-bar">
          <span>
            {stockFBMData.length > 0 ? `Stock FBM: ${stockFBMData.length} produits` : 'Stock FBM: Non importé'}
          </span>
          <span>
            {stockFBAData.length > 0 ? `Stock FBA: ${stockFBAData.length} produits` : 'Stock FBA: Non importé'}
          </span>
          <span>
            {salesData.length > 0 ? `Données de vente: ${salesData.length} produits` : 'Données de vente: Non importées'}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;